import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Sparkle, House, Heart, Briefcase, Palette, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import MultiPhotoPicker from "@/components/MultiPhotoPicker";
import AILoadingOverlay from "@/components/AILoadingOverlay";
import { toast } from "sonner";

// Project-type selector at the top of the creation flow. Only `home` triggers
// the real AI planning flow — the three non-home types route to a feedback
// capture screen so we can measure demand BEFORE expanding scope.
// "Home Job" is the recommended default and visually highlighted.
const PROJECT_TYPES = [
  { key: "home",     label: "Home Job",         hint: "Half Job's specialty",  icon: House,        recommended: true },
  { key: "personal", label: "Personal Project", hint: "Not yet supported",      icon: Heart,        recommended: false },
  { key: "work",     label: "Work Project",     hint: "Not yet supported",      icon: Briefcase,    recommended: false },
  { key: "hobby",    label: "Hobby Project",    hint: "Not yet supported",      icon: Palette,      recommended: false },
];

export default function ProjectNewPage() {
  const [projectType, setProjectType] = useState("home");
  // For non-home types we collect feedback in two stages: "form" then "thanks".
  const [feedbackStage, setFeedbackStage] = useState("form"); // "form" | "thanks"
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [photoIds, setPhotoIds] = useState([]);
  const [alreadyDone, setAlreadyDone] = useState("");
  const [notStarted, setNotStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  // AbortController kept in a ref so the AILoadingOverlay Cancel button can abort
  // the in-flight axios call (axios cancels via the underlying fetch when the
  // signal aborts). When abort fires we silently bail to the form — no toast,
  // because the user just told us they want to stop.
  const abortRef = useRef(null);
  const cancelledRef = useRef(false);
  const navigate = useNavigate();

  // When "I haven't started" is ticked, blank the field so the AI receives
  // unambiguous signal (no `already_done` payload sent).
  const onToggleNotStarted = (checked) => {
    setNotStarted(checked);
    if (checked) setAlreadyDone("");
  };

  const cancelBreakdown = () => {
    if (abortRef.current) {
      cancelledRef.current = true;
      abortRef.current.abort();
    }
    setBusy(false);
  };

  // Submit the non-Home Job feedback. No project is created — we just
  // persist the user's words so we can measure demand for future scope.
  const submitFeedback = async () => {
    if (!feedbackText.trim()) {
      toast.error("Tell us what you were hoping Half Job could help with.");
      return;
    }
    try {
      setSubmittingFeedback(true);
      await api.post("/future-requests", {
        selected_project_type: projectType,
        title: title.trim() || null,
        description: description.trim() || null,
        feedback: feedbackText.trim(),
      });
      setFeedbackStage("thanks");
    } catch {
      toast.error("Couldn't send that. Try again in a moment.");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const [refusal, setRefusal] = useState(null); // { code, title, message }

  const runBreakdown = async () => {
    if (!title.trim()) {
      toast.error("Add a short title first");
      return;
    }
    cancelledRef.current = false;
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setBusy(true);
      const r = await api.post(
        "/projects/ai-breakdown",
        {
          title: title.trim(),
          description: description.trim(),
          photo_ids: photoIds,
          already_done: notStarted ? null : (alreadyDone.trim() || null),
        },
        { signal: controller.signal },
      );
      const draft = r.data;
      sessionStorage.setItem("halfjob_draft", JSON.stringify({
        ...draft,
        title: draft.title || title.trim(),
        description: draft.description || description.trim(),
        location: draft.location || location.trim(),
        cover_photo_id: photoIds[0] || null,
        photo_ids: photoIds,
      }));
      navigate("/projects/new/review");
    } catch (e) {
      // Cancelled by user — fall back silently to the form.
      if (cancelledRef.current || e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return;
      }
      const detail = e?.response?.data?.detail;
      // Server-side scope/safety refusal returns a structured 422 we surface in a dialog.
      if (detail && typeof detail === "object" && detail.message && detail.code) {
        setRefusal({
          code: detail.code,
          title: detail.title || "That one's not in our tool belt.",
          message: detail.message,
        });
      } else if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error("AI breakdown failed. You can still save manually.");
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const saveManual = async () => {
    if (!title.trim()) {
      toast.error("Add a title first");
      return;
    }
    try {
      setBusy(true);
      const r = await api.post("/projects", {
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        cover_photo_id: photoIds[0] || null,
        photo_ids: photoIds,
      });
      navigate(`/projects/${r.data.id}`, { replace: true });
    } catch {
      toast.error("Could not create project");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="project-new-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-timber-700 font-bold" data-testid="back-btn">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        {projectType === "home" && (
          <>
            <p className="label-cap mt-3" data-testid="new-step-indicator">Step 1 of 4 · Plan job</p>
            <h1 className="font-heading text-3xl font-bold mt-1">Plan a job</h1>
            <p className="text-timber-700 text-sm mt-1">
              A few words is enough. Add a few photos and your AI copilot will draft the plan.
            </p>
          </>
        )}
      </header>

      {/* Project type selector — always at the top. Home Job is the default + recommended. */}
      <section className="px-5 max-w-md mx-auto w-full" data-testid="project-type-selector">
        <Label className="label-cap">What kind of project?</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {PROJECT_TYPES.map((t) => {
            const Icon = t.icon;
            const selected = projectType === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`project-type-${t.key}`}
                onClick={() => { setProjectType(t.key); setFeedbackStage("form"); setFeedbackText(""); }}
                className={[
                  "relative text-left rounded-2xl border-2 p-3 transition-all btn-press",
                  selected
                    ? "bg-chalk border-masking shadow-tactile"
                    : "bg-chalk/60 border-stone_border",
                ].join(" ")}
              >
                {t.recommended && (
                  <span className="absolute -top-2 right-2 bg-masking text-timber-900 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border-2 border-timber-900">
                    Recommended
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <Icon
                    size={20}
                    weight={selected ? "fill" : "duotone"}
                    className={selected ? "text-masking" : "text-timber-700"}
                  />
                  <span className="font-bold text-timber-900 text-sm">{t.label}</span>
                  {selected && <Check size={14} weight="bold" className="ml-auto text-masking" />}
                </div>
                <p className="text-[11px] text-timber-700 mt-1">{t.hint}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Home Job → full AI planning flow (unchanged) */}
      {projectType === "home" && (
      <main className="px-5 max-w-md mx-auto w-full space-y-4 mt-5">
        <div>
          <Label className="label-cap">{`What's the job?`}</Label>
          <Input
            data-testid="new-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Fix gaps in skirting board"
            className="min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl text-lg"
          />
        </div>
        <div>
          <Label className="label-cap">Tell us a little more (optional)</Label>
          <Textarea
            data-testid="new-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Explain a little more about what you're trying to achieve."
            className="mt-1 bg-chalk border-2 border-stone_border rounded-xl text-base"
          />
        </div>
        <div>
          <Label className="label-cap">Room / location</Label>
          <Input
            data-testid="new-location-input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. Living room"
            className="min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl text-lg"
          />
        </div>

        {/* Progress so far (optional). Tick "I haven't started" to skip;
            otherwise text feeds the breakdown prompt and matching steps land
            pre-completed. */}
        <div>
          <Label className="label-cap">Progress so far (optional)</Label>
          <Textarea
            data-testid="already-done-input"
            value={alreadyDone}
            onChange={(e) => setAlreadyDone(e.target.value)}
            rows={2}
            disabled={notStarted}
            placeholder={notStarted ? "Skipped — starting from scratch" : "What have you already done? What still needs doing?"}
            className={`mt-1 bg-chalk border-2 border-stone_border rounded-xl text-base ${notStarted ? "opacity-40" : ""}`}
          />
          <label
            className="mt-2 flex items-center gap-2 text-sm font-bold text-timber-900 cursor-pointer select-none"
            data-testid="not-started-checkbox-label"
          >
            <input
              type="checkbox"
              data-testid="not-started-checkbox"
              checked={notStarted}
              onChange={(e) => onToggleNotStarted(e.target.checked)}
              className="w-5 h-5 accent-masking"
            />
            {`I haven't started yet`}
          </label>
        </div>

        <div>
          <Label className="label-cap">Photos (helps the AI)</Label>
          <div className="mt-2">
            <MultiPhotoPicker
              testidBase="new-photo"
              value={photoIds}
              onChange={setPhotoIds}
              max={10}
              disabled={busy}
            />
            <p className="text-[11px] text-timber-500 mt-1">
              Up to 10 photos. The AI looks at the first 5 — wide shot + close-ups work best.
            </p>
          </div>
        </div>

        <Button
          data-testid="ai-breakdown-btn"
          onClick={runBreakdown}
          disabled={busy}
          className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[64px] text-lg font-bold btn-press"
        >
          <Sparkle size={20} weight="fill" className="mr-2" />
          {busy ? "Building your plan…" : "Create My Plan"}
        </Button>
        <button
          data-testid="manual-save-btn"
          onClick={saveManual}
          disabled={busy}
          className="w-full text-sm text-timber-700 underline font-semibold"
        >
          {`Skip — I'll add steps myself`}
        </button>
      </main>
      )}

      {/* Non-home job → feedback capture screen (no project created). */}
      {projectType !== "home" && feedbackStage === "form" && (
        <main className="px-5 max-w-md mx-auto w-full mt-6" data-testid="future-feedback-form">
          <div className="tactile-card p-5 border-2 border-denim">
            <h2 className="font-heading text-2xl font-bold text-timber-900 leading-tight" data-testid="future-feedback-title">
              {`That one's not in our tool belt… yet.`}
            </h2>
            <p className="text-timber-700 text-sm mt-3 leading-relaxed">
              Half Job is currently focused on helping people finish Home Jobs.
            </p>
            <p className="text-timber-700 text-sm mt-2 leading-relaxed">
              {`We're curious though…`}
            </p>
            <p className="text-timber-900 text-sm font-bold mt-2 leading-relaxed">
              What were you hoping Half Job could help you finish?
            </p>
            <Textarea
              data-testid="future-feedback-textarea"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={7}
              placeholder={`For example:\n• Build my startup\n• Launch a website\n• Organise my finances\n• Write a book\n• Learn guitar\n• Plan a holiday`}
              className="mt-3 bg-chalk border-2 border-stone_border rounded-xl text-base"
            />
            <p className="text-[11px] text-timber-500 mt-2">
              Your feedback helps shape what Half Job builds next.
            </p>
            <Button
              data-testid="future-feedback-submit"
              onClick={submitFeedback}
              disabled={submittingFeedback}
              className="w-full mt-4 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] text-base font-bold btn-press"
            >
              {submittingFeedback ? "Sending…" : "Send Feedback"}
            </Button>
            <button
              type="button"
              data-testid="future-feedback-later"
              onClick={() => setProjectType("home")}
              className="w-full mt-2 text-sm text-timber-700 font-semibold py-3"
            >
              Maybe Later
            </button>
          </div>
        </main>
      )}

      {projectType !== "home" && feedbackStage === "thanks" && (
        <main className="px-5 max-w-md mx-auto w-full mt-6" data-testid="future-feedback-thanks">
          <div className="tactile-card p-6 text-center border-2 border-dashed border-masking">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-timber-900 flex items-center justify-center shadow-tactile">
              <Check size={32} weight="bold" className="text-masking" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-timber-900 mt-4 leading-tight">
              Thanks for helping shape Half Job.
            </h2>
            <p className="text-timber-700 text-sm mt-3 leading-relaxed">
              {`We've added it to the roadmap pile.`}
            </p>
            <p className="text-timber-700 text-sm mt-2 leading-relaxed">
              {`No promises yet… but we'll keep an eye on what people ask for.`}
            </p>
            <Button
              data-testid="future-feedback-home"
              onClick={() => navigate("/")}
              className="mt-5 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] px-6 font-bold btn-press"
            >
              Back to Home
            </Button>
          </div>
        </main>
      )}

      <AILoadingOverlay open={busy} testidBase="plan-loading" onCancel={cancelBreakdown} />

      {/* Scope / safety refusal dialog — brand-aligned copy supplied by the API. */}
      <Dialog open={!!refusal} onOpenChange={(o) => { if (!o) setRefusal(null); }}>
        <DialogContent
          data-testid={`scope-refusal-dialog-${refusal?.code || ""}`}
          className="bg-chalk border-2 border-stone_border max-w-sm"
        >
          <DialogHeader>
            <DialogTitle
              data-testid="scope-refusal-title"
              className="font-heading text-2xl text-timber-900 leading-tight"
            >
              {refusal?.title}
            </DialogTitle>
            <DialogDescription
              data-testid="scope-refusal-message"
              className="text-timber-700 text-sm leading-relaxed whitespace-pre-line pt-2"
            >
              {refusal?.message}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              data-testid="scope-refusal-ok"
              onClick={() => setRefusal(null)}
              className="w-full bg-timber-900 text-masking hover:bg-timber-900 min-h-[52px] font-bold"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
